import { MutationSet } from 'src/common/interfaces';
import { RecordState, RegisterState } from 'src/common/string-enum';
import { Option } from 'src/components/quotation-input/selection';
import { ErrorMessage, InfoMessage, InfoWord } from 'src/enum/messsage-enum';
import Swal, { SweetAlertIcon } from 'sweetalert2';

const showAlertMessage = async (
  title: string,
  text: string,
  icon: SweetAlertIcon,
  confirmButtonText: string,
  html = ''
) => {
  await Swal.fire({
    title,
    text,
    icon,
    confirmButtonText,
    html,
  });
};

const confirmAlert = (
  title: string,
  text: string,
  icon: SweetAlertIcon,
  confirmButtonText: string,
  cancelButtonText: string,
  onPress: () => void
) => {
  Swal.fire({
    title,
    text,
    icon,
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
  }).then((result) => {
    if (result.isConfirmed) {
      onPress();
    }
  });
};

type RegisterErrMessage = {
  [key in RecordState]?: { data: any; message: string }[];
};

const executeMutations = async <DataType>(
  mutationSet: MutationSet<DataType>
): Promise<[RegisterState, RegisterErrMessage | undefined]> => {
  const messages: { data: DataType; message: string }[] = [];
  const results = await Promise.all(
    mutationSet.data.map((data) => {
      try {
        return mutationSet.func(data);
      } catch (error: any) {
        return messages.push({ data: data, message: error.message });
      }
    })
  );

  let registerState;
  const errCount = results.filter((el) => {
    return el === undefined;
  }).length;

  if (errCount === 0) {
    registerState = RegisterState.SUCCESS;
  } else if (errCount === mutationSet.data.length) {
    registerState = RegisterState.FAILURE;
  } else {
    registerState = RegisterState.PARTIAL_FAILURE;
  }

  return [registerState, messages.length > 0 ? { [mutationSet.recordState]: messages } : undefined];
};

const registerData = async <T1, T2 = T1>({
  mutationSets1,
  mutationSets2,
  validates,
  searchFunc,
}: {
  mutationSets1: MutationSet<T1>[];
  mutationSets2?: MutationSet<T2>[];
  validates: () => boolean;
  searchFunc: () => Promise<any>;
}) => {
  if (!validates()) return;

  const regState: RegisterState[] = [];
  const errMessags: RegisterErrMessage[] = [];

  mutationSets1.forEach(async (mutationSet) => {
    if (mutationSet.data.length <= 0) return;
    const execution = await executeMutations(mutationSet);
    regState.push(execution[0]);
    if (execution[1]) errMessags.push(execution[1]);
  });

  mutationSets2?.forEach(async (mutationSet) => {
    if (mutationSet.data.length <= 0) return;
    const execution = await executeMutations(mutationSet);
    regState.push(execution[0]);
    if (execution[1]) errMessags.push(execution[1]);
  });

  // 処理結果に応じたメッセージ対応

  // 登録状態の配列の要素が全て成功の場合
  if (regState.every((el) => el === RegisterState.SUCCESS)) {
    await showAlertMessage('', InfoMessage.REGISTR_SUCCESS, InfoWord.SUCCESS, InfoWord.OK);
    await searchFunc();

    // 成功の場合は、エラーメッセージ出力の必要が無いので処理終了
    return;
  }

  // 登録状態の配列の要素が全て失敗の場合
  if (regState.every((el) => el === RegisterState.FAILURE)) {
    showAlertMessage('', ErrorMessage.REGISTR_FAILED, InfoWord.ERROR, InfoWord.OK);
    await searchFunc();

    // エラーログをコンソールに出力
    errMessags.forEach((message) => console.log(message));

    return;
  }

  // 削除、更新、挿入のどれかで失敗が含まれている場合
  if (regState.includes(RegisterState.FAILURE) || regState.includes(RegisterState.PARTIAL_FAILURE)) {
    showAlertMessage('', ErrorMessage.REGISTR_FAILED_PART, InfoWord.ERROR, InfoWord.OK);
    await searchFunc();

    // エラーログをコンソールに出力
    errMessags.forEach((message) => console.log(message));

    return;
  }
};

const mapOption = (selected: string | number | undefined, options: Array<Option>) => {
  return options.find((option: Option) => selected?.toString() === option.value.toString());
};

const utility = {
  showAlertMessage,
  confirmAlert,
  registerData,
  mapOption,
};

export default utility;
